import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { useTranslation } from '@wix/cli-i18n';
import type { SelectOption } from '@wix/cli-ui-kit';
import {
  Spinner,
  SelectInput,
  useAsync,
  SelectOptionAvailability,
} from '@wix/cli-ui-kit';
import { useDevCenterClient } from '@wix/dev-center-client';
import type { Site } from '@wix/site-list-client';
import { useSiteSelectorBi } from './bi.js';

export type SiteType =
  | 'create-dev-site'
  | 'create-harmony-site'
  | 'select-existing-site';
interface Props {
  onSubmit: (siteType: SiteType) => void;
  onError: (error: unknown) => void;
  sites: Site[];
}

export const SiteTypeSelect: FC<Props> = ({ onSubmit, onError, sites }) => {
  const bi = useSiteSelectorBi();
  const { t } = useTranslation();
  const { getDevSitesCount, getDevSitesLimit } = useDevCenterClient();
  const { status } = useAsync(
    () => Promise.all([getDevSitesCount(), getDevSitesLimit()]),
    [],
    { onError }
  );

  const handleSubmit = (option: SelectOption<SiteType>) => {
    void bi.cliFlowStepAnswered({
      question: t('site_type_select.label'),
      answer: option.title,
    });
    onSubmit(option.value);
  };

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={t('general.loading')} />,
    Success: ({ result: [devSitesCount, devSitesLimit] }) => {
      const isDevSitesLimitReached = devSitesCount >= devSitesLimit;
      const userHasNoSites = sites.length === 0;

      return (
        <SelectInput<SiteType>
          label={t('site_type_select.label')}
          options={[
            {
              availability: userHasNoSites
                ? SelectOptionAvailability.Disabled({
                    reason: t('site_type_select.no_sites'),
                  })
                : SelectOptionAvailability.Enabled(),
              title: t('site_type_select.pick_existing_site'),
              value: 'select-existing-site',
            },
            {
              title: t('site_type_select.create_new_dev_site'),
              value: 'create-dev-site',
              availability: isDevSitesLimitReached
                ? SelectOptionAvailability.Disabled({
                    reason: t('site_type_select.create_new_dev_site.disabled', {
                      limit: devSitesLimit,
                    }),
                  })
                : SelectOptionAvailability.Enabled(),
            },
            {
              title: t('site_type_select.create_new_harmony_site'),
              value: 'create-harmony-site',
              availability: isDevSitesLimitReached
                ? SelectOptionAvailability.Disabled({
                    reason: t('site_type_select.create_new_dev_site.disabled', {
                      limit: devSitesLimit,
                    }),
                  })
                : SelectOptionAvailability.Enabled(),
            },
          ]}
          onSubmit={handleSubmit}
        />
      );
    },
  });
};
